import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl, API, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import Header from "../components/Header";
import { ClienteLogin } from "../components/ClienteLogin";
import { LogOut, FileText, XCircle, Clock, CheckCircle2, Star, Bell, User } from "lucide-react";
import { connectWS } from "../lib/ws";
import { inscreverPush } from "../lib/push";
import { ChatCorrida } from "./DriverApp";

function AvaliarMotoristaBloco({ jobId, motorista, token, onDone }) {
  const [estrelas, setEstrelas] = useState(0);
  const [comentario, setComentario] = useState("");
  const [busy, setBusy] = useState(false);
  const enviar = async () => {
    if (!estrelas) return toast.error("Selecione de 1 a 5 estrelas");
    setBusy(true);
    try {
      await api.post("/cliente/avaliar", { job_id: jobId, estrelas, comentario }, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      toast.success("Avaliação enviada! Obrigado.");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao avaliar");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2 bg-gold-light border border-[#F5B301]/60 rounded-xl p-3" data-testid={`avaliar-motorista-${jobId}`}>
      <p className="text-xs font-bold text-navy mb-1.5">Avalie o motorista{motorista ? ` ${motorista}` : ""}:</p>
      <div className="flex gap-1.5 mb-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} data-testid={`estrela-motorista-${jobId}-${n}`} onClick={() => setEstrelas(n)}>
            <Star className={`w-7 h-7 ${n <= estrelas ? "fill-[#F5B301] text-[#F5B301]" : "text-slate-300"}`} />
          </button>
        ))}
      </div>
      <input data-testid={`comentario-motorista-${jobId}`} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs mb-2"
        placeholder="Comentário (opcional)" value={comentario} onChange={(e) => setComentario(e.target.value)} />
      <button data-testid={`enviar-avaliacao-motorista-${jobId}`} onClick={enviar} disabled={busy}
        className="w-full py-2.5 rounded-xl bg-navy text-[#F5B301] text-xs font-bold active:scale-95 transition-transform disabled:opacity-50">
        ENVIAR AVALIAÇÃO
      </button>
    </div>
  );
}

const fmtD = (s) => (s ? s.split("-").reverse().join("/") : "-");

const STATUS_ICON = {
  pix_informado: <Clock className="w-4 h-4 text-amber-500" />,
  reservada: <CheckCircle2 className="w-4 h-4 text-emerald-500" />,
  recusada: <XCircle className="w-4 h-4 text-red-500" />,
  expirada: <XCircle className="w-4 h-4 text-slate-400" />,
};

const EM_ANDAMENTO_STATUS = ["aceita", "a_caminho", "chegou_origem", "carga_iniciada", "em_rota", "chegou_destino"];
const CANCELADOS_STATUS = ["recusada", "expirada", "cancelada"];

const grupoDe = (s) => {
  if (CANCELADOS_STATUS.includes(s.status)) return "cancelados";
  if (s.job_status === "concluida") return "concluidos";
  if (EM_ANDAMENTO_STATUS.includes(s.job_status)) return "andamento";
  if (s.status === "pix_informado") return "pendentes";
  return "confirmados";
};

export default function ClientArea() {
  const [token, setToken] = useState(localStorage.getItem("client_token") || "");
  const [googleUser, setGoogleUser] = useState(null);
  const [chatJob, setChatJob] = useState(null);
  const [nome, setNome] = useState("");
  const [servicos, setServicos] = useState(null);
  const [notifBanner, setNotifBanner] = useState(false);

  const ativarNotificacoes = async () => {
    const ok = await inscreverPush("/push/subscribe/cliente");
    if (ok) { toast.success("Notificações ativadas! Você receberá avisos do seu frete mesmo com o app fechado."); setNotifBanner(false); }
    else toast.error("Não foi possível ativar. Verifique a permissão de notificações do navegador.");
  };

  const load = (t, g) => {
    const req = g
      ? api.get("/cliente/google/servicos")
      : api.get("/cliente/servicos", { headers: { Authorization: `Bearer ${t}` } });
    req.then((r) => { setServicos(r.data.servicos); })
      .catch(() => { localStorage.removeItem("client_token"); setToken(""); setGoogleUser(null); });
  };
  useEffect(() => { if (token) load(token, null); }, [token]);
  useEffect(() => {
    if (!localStorage.getItem("client_token")) {
      api.get("/cliente/google/me").then((r) => { setGoogleUser(r.data); setNome(r.data.nome); }).catch(() => {});
    }
  }, []);
  useEffect(() => { if (googleUser) load(null, googleUser); }, [googleUser]);
  useEffect(() => {
    if (!token && !googleUser) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "granted") inscreverPush("/push/subscribe/cliente");
    else if (Notification.permission === "default") setNotifBanner(true);
  }, [token, googleUser]);
  useEffect(() => {
    if (!token && !googleUser) return;
    const close = connectWS((ev) => {
      if (["status_corrida", "reserva_atualizada", "recebimento_confirmado"].includes(ev.tipo)) load(token, googleUser);
    });
    return close;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, googleUser]);

  const sair = () => {
    if (googleUser) { api.post("/auth/logout").catch(() => {}); setGoogleUser(null); }
    localStorage.removeItem("client_token");
    setToken("");
    setServicos(null);
    setNome("");
  };

  const grupos = { pendentes: [], confirmados: [], andamento: [], concluidos: [], cancelados: [] };
  (servicos || []).forEach((s) => grupos[grupoDe(s)].push(s));

  const Card = ({ s }) => (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={`servico-card-${s.reservation_id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-heading font-bold text-navy text-sm">{s.numero_reserva} · {s.tipo === "frete" ? "Frete" : "Mudança"}</span>
        <span className="flex items-center gap-1 text-[10px] font-bold text-slate-500">{STATUS_ICON[s.status]}{s.status_label?.split("—")[0]}</span>
      </div>
      <p className="text-xs text-slate-600">{fmtD(s.data)} às {s.horario}</p>
      <p className="text-xs text-slate-600">{s.origem} → {s.destino}</p>
      {s.motorista && (
        <div className="mt-2 bg-slate-50 border border-slate-100 rounded-xl p-3 flex items-center gap-3" data-testid={`motorista-info-${s.reservation_id}`}>
          {s.motorista.foto_url ? (
            <img src={`${BACKEND_URL}${s.motorista.foto_url}`} alt={s.motorista.nome} className="w-12 h-12 rounded-full object-cover border-2 border-[#F5B301]" data-testid={`motorista-foto-${s.reservation_id}`} />
          ) : (
            <div className="w-12 h-12 rounded-full bg-navy text-[#F5B301] flex items-center justify-center font-heading font-bold">
              {s.motorista.nome?.[0] || "M"}
            </div>
          )}
          <div className="text-xs">
            <p className="font-bold text-navy">{s.motorista.nome}</p>
            <p className="text-slate-500">{s.motorista.placa ? `Veículo placa ${s.motorista.placa} · ` : ""}{s.motorista.telefone}</p>
          </div>
        </div>
      )}
      {s.motorista_nome && <p className="text-xs text-slate-600">Motorista: <strong>{s.motorista_nome}</strong></p>}
      {s.job_id && s.motorista_nome && (
        <>
          <button data-testid={`chat-corrida-cliente-${s.job_id}`} onClick={() => setChatJob({ id: s.job_id, sub: `${s.origem} → ${s.destino} · ${fmtD(s.data)} ${s.horario}` })}
            className="mt-2 w-full py-2.5 rounded-xl bg-navy text-[#F5B301] text-[11px] font-extrabold active:scale-95 transition-transform">
            💬 CHAT COM O MOTORISTA
          </button>
          <p className="text-[10px] text-slate-400 leading-snug mt-1">
            No dia da sua corrida, qualquer dúvida ou necessidade de contato com o motorista poderá ser resolvida diretamente pelo chat do aplicativo. Você não precisa compartilhar seu número de telefone.
          </p>
        </>
      )}
      <p className="text-xs text-slate-600 mt-1">Total: <strong>{brl(s.total)}</strong> · Sinal: {brl(s.sinal)} · Saldo: {brl(s.saldo)}</p>
      {s.cancel_motivo && (
        <p className="text-[11px] text-red-600 bg-red-50 rounded-lg p-2 mt-2" data-testid={`cancel-motivo-${s.reservation_id}`}>❌ AGENDAMENTO CANCELADO — {s.cancel_motivo}</p>
      )}
      {s.job_status === "concluida" && (
        <div className="mt-2 space-y-2" data-testid={`avaliacao-pos-servico-${s.reservation_id}`}>
          {!s.avaliado_cliente && (!s.recebimento || s.recebimento.confirmado) && (
            <AvaliarMotoristaBloco jobId={s.job_id} motorista={s.motorista_nome} token={token} onDone={() => load(token)} />
          )}
          {s.google_review_url && (
            <a data-testid={`avaliar-google-${s.reservation_id}`} href={s.google_review_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 w-full py-2.5 rounded-xl bg-navy text-[#F5B301] text-[11px] font-bold active:scale-95 transition-transform">
              ⭐ AVALIAR NO GOOGLE
            </a>
          )}
        </div>
      )}
      <a data-testid={`servico-pdf-${s.reservation_id}`} href={`${API}/reservations/${s.reservation_id}/pdf`} target="_blank" rel="noopener noreferrer"
        className="mt-2 flex items-center justify-center gap-1.5 w-full py-2 rounded-xl border border-slate-200 text-navy text-[11px] font-bold">
        <FileText className="w-3.5 h-3.5" /> CONTRATO (PDF)
      </a>
    </div>
  );

  const Grupo = ({ titulo, lista, tid }) => (
    <section>
      <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2">{titulo}</h2>
      <div className="space-y-3" data-testid={tid}>
        {lista.length === 0 && <p className="text-xs text-slate-400">Nenhum agendamento neste estado.</p>}
        {lista.map((s) => <Card key={s.reservation_id} s={s} />)}
      </div>
    </section>
  );

  return (
    <div className="min-h-screen bg-[#0b1220] flex justify-center">
      <div className="w-full max-w-md mx-auto min-h-screen bg-slate-50 shadow-2xl pb-10" data-testid="client-area">
        <Header right={(token || googleUser) && <button data-testid="client-logout-btn" onClick={sair} className="p-2 text-slate-400 hover:text-red-500"><LogOut className="w-5 h-5" /></button>} />
        {chatJob && (
          <ChatCorrida jobId={chatJob.id} role="cliente" token={token} onClose={() => setChatJob(null)} subtitulo={chatJob.sub} />
        )}

        {!token && !googleUser ? (
          <ClienteLogin
            titulo="Consultar Agendamento"
            subtitulo="Entre com sua conta Google ou com o telefone e o CPF informados no orçamento para consultar seus agendamentos."
            redirectPath="/meus-servicos"
            prefix="client"
            onToken={(t, n) => { setNome(n); setToken(t); }}
          />
        ) : servicos === null ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#F5B301] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="px-4 pt-5 space-y-6">
            <div className="flex items-center justify-between">
              <span className="inline-block text-[10px] font-extrabold bg-navy text-[#F5B301] px-3 py-1 rounded-full uppercase tracking-wide" data-testid="perfil-cliente-label">
                👤 Cliente
              </span>
            </div>
            {nome && <p className="text-sm text-slate-500">Olá, <strong className="text-navy">{nome}</strong>.</p>}
            <Link to="/minha-conta" data-testid="consultar-minha-conta-btn"
              className="w-full p-4 rounded-2xl bg-navy flex items-center gap-3 active:scale-[0.98] transition-transform">
              <User className="w-5 h-5 text-[#F5B301] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-[#F5B301] text-xs">MINHA CONTA</p>
                <p className="text-[10px] text-slate-300">Dados pessoais, orçamentos e suas áreas (Motorista / Clube de Indicação)</p>
              </div>
            </Link>
            {notifBanner && (
              <div className="bg-white rounded-2xl border border-[#F5B301]/60 shadow-sm p-4 flex items-center gap-3" data-testid="cliente-notif-banner">
                <Bell className="w-6 h-6 text-[#F5B301] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-navy">Ative as notificações</p>
                  <p className="text-[10px] text-slate-500">Receba avisos do seu frete mesmo com o app fechado.</p>
                </div>
                <button data-testid="cliente-notif-ativar-btn" onClick={ativarNotificacoes}
                  className="py-2 px-3 rounded-xl bg-navy text-[#F5B301] text-[10px] font-extrabold active:scale-95 transition-transform shrink-0">
                  ATIVAR
                </button>
                <button data-testid="cliente-notif-fechar-btn" onClick={() => setNotifBanner(false)} className="text-slate-300 hover:text-slate-500 text-sm shrink-0 px-1">✕</button>
              </div>
            )}

            <Grupo titulo="Agendamentos pendentes" lista={grupos.pendentes} tid="ag-pendentes-list" />
            <Grupo titulo="Agendamentos confirmados" lista={grupos.confirmados} tid="ag-confirmados-list" />
            <Grupo titulo="Agendamentos em andamento" lista={grupos.andamento} tid="ag-andamento-list" />
            <Grupo titulo="Agendamentos concluídos" lista={grupos.concluidos} tid="ag-concluidos-list" />
            {grupos.cancelados.length > 0 && (
              <Grupo titulo="Agendamentos cancelados" lista={grupos.cancelados} tid="ag-cancelados-list" />
            )}

            <p className="text-center text-[10px] text-slate-400">
              Para orçamentos em andamento, dados da conta e outras áreas, acesse <Link to="/minha-conta" className="font-bold text-navy underline" data-testid="consultar-minha-conta-link">MINHA CONTA</Link>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
